import { useAuth, useTranslation } from '@/context/AuthContext';
import { useState, useEffect } from 'react';
import { getTasks, createTask, updateTask, deleteTask } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Tasks() {
  const { families } = useAuth();
  const { t } = useTranslation();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', priority: 'medium', due_date: '' });

  const familyId = families[0]?.family_id;

  const load = async () => {
    if (!familyId) return;
    try {
      const res = await getTasks({ family_id: familyId });
      setTasks(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [familyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    if (!form.title || !familyId) return;
    await createTask({ ...form, family_id: familyId, status: 'todo' });
    setForm({ title: '', priority: 'medium', due_date: '' });
    setOpen(false);
    toast.success('Task created');
    load();
  };

  const handleStatus = async (id, status) => {
    await updateTask(id, { status });
    load();
  };

  const handleDelete = async (id) => {
    await deleteTask(id);
    toast.success('Task deleted');
    load();
  };

  const statuses = ['todo', 'in_progress', 'done'];

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-[#E2E8F0] rounded-2xl animate-pulse" />)}</div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl md:text-3xl text-[#1E293B]" >
          {t('tasks.title')}
        </h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white" data-testid="create-task-global-btn">
              <Plus className="w-4 h-4 mr-2" /> {t('tasks.create')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('tasks.create')}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm">Title</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 h-11" data-testid="global-task-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">{t('tasks.priority')}</Label>
                  <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}>
                    <SelectTrigger className="mt-1 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">{t('tasks.high')}</SelectItem>
                      <SelectItem value="medium">{t('tasks.medium')}</SelectItem>
                      <SelectItem value="low">{t('tasks.low')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">{t('tasks.dueDate')}</Label>
                  <Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} className="mt-1 h-11" />
                </div>
              </div>
              <Button onClick={handleCreate} className="w-full rounded-full bg-[#3A86FF] hover:bg-[#3A86FF]/90 text-white" data-testid="save-global-task-btn">{t('common.create')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {statuses.map(status => {
          const statusTasks = tasks.filter(t => t.status === status);
          return (
            <div key={status}>
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-2.5 h-2.5 rounded-full ${status === 'todo' ? 'bg-blue-500' : status === 'in_progress' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                <span className="text-sm font-semibold text-[#1E293B]">
                  {t(`tasks.${status === 'in_progress' ? 'inProgress' : status}`)}
                </span>
                <Badge variant="secondary" className="text-[10px] rounded-full ml-auto">{statusTasks.length}</Badge>
              </div>
              <div className="space-y-2 min-h-[200px] bg-[#F1F5F9]/40 rounded-2xl p-3">
                {statusTasks.map(task => (
                  <Card key={task.task_id} className="border border-[#E2E8F0]/40 rounded-xl hover:shadow-sm transition-shadow" data-testid={`gtask-${task.task_id}`}>
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between mb-2">
                        <p className="text-sm font-medium text-[#1E293B] flex-1">{task.title}</p>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(task.task_id)} className="h-6 w-6 p-0 text-[#E2E8F0] hover:text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full ${task.priority === 'high' ? 'bg-red-500' : task.priority === 'medium' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                          <span className="text-[10px] text-[#64748B]">{t(`tasks.${task.priority}`)}</span>
                        </div>
                        <Select value={task.status} onValueChange={v => handleStatus(task.task_id, v)}>
                          <SelectTrigger className="h-6 text-[10px] w-20 border-0 bg-[#F1F5F9] rounded-full"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todo">{t('tasks.todo')}</SelectItem>
                            <SelectItem value="in_progress">{t('tasks.inProgress')}</SelectItem>
                            <SelectItem value="done">{t('tasks.done')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {task.due_date && (
                        <p className="text-[10px] text-[#64748B] mt-1">{task.due_date}</p>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
